import {
    CreditCardOutlined,
    AppstoreOutlined,
    KeyOutlined,
    PercentageOutlined,
    ReloadOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {
    Alert,
    Button,
    Card,
    DatePicker,
    Empty,
    Input,
    Select,
    Table,
    Tag,
    Tooltip,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useThemeMode } from "../../context/ThemeContext";
import CustomHeader from "../common/Header";
import HTTP from "../../services/httpService";
import "./KeyInsights.css";

dayjs.extend(utc);
dayjs.extend(timezone);
const formatCount = (value) =>
    value == null ? "—" : Number(value).toLocaleString("en-US");
const rangeOptions = [
    { value: "7", label: "Last 7 days" },
    { value: "30", label: "Last 30 days" },
    { value: "90", label: "Last 90 days" },
    { value: "custom", label: "Custom dates" },
];
const keyGroupOptions = [
    { value: "all", label: "All keys" },
    { value: "new", label: "New keys at check-in" },
    { value: "reissued", label: "Duplicate / re-issued keys" },
    { value: "other", label: "Other keys" },
];
const operationLabels = {
    1: "Check-in",
    2: "Walk-in",
    4: "Check-out",
    5: "Duplicate / re-issued",
};
const statusLabels = { 1: "In progress", 2: "Aborted", 3: "Completed" };

const KeyInsights = () => {
    const { mode } = useThemeMode();
    const isDark = mode === "dark";
    const staffId = localStorage.getItem("x-dashboard-staffid");
    const [hotelIds, setHotelIds] = useState([]);
    const [range, setRange] = useState("30");
    const [dates, setDates] = useState([
        dayjs().subtract(29, "day").startOf("day"),
        dayjs().endOf("day"),
    ]);
    const [keyGroup, setKeyGroup] = useState("all");
    const transactionsRef = useRef(null);
    const refreshInFlightRef = useRef(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [sort, setSort] = useState({ field: "issuedAt", order: "descend" });
    useEffect(() => {
        if (search.trim() === searchQuery) return;
        const timer = setTimeout(() => {
            setSearchQuery(search.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search, searchQuery]);
    const hotelsQuery = useQuery(
        ["key-insights-hotels", staffId],
        async ({ signal }) => {
            const response = await HTTP(
                "/dashboard/getHotels",
                {
                    staff_id: staffId,
                    hotel_type: "all",
                },
                "POST",
                { signal },
            );
            if (!response?.success)
                throw new Error(response?.message || "Unable to load hotels.");
            return response.data || [];
        },
        {
            enabled: Boolean(staffId),
            retry: false,
            refetchOnWindowFocus: false,
        },
    );
    const hotels = Array.isArray(hotelsQuery.data) ? hotelsQuery.data : [];
    const startDate = dates[0].format("YYYY-MM-DD");
    const endDate = dates[1].format("YYYY-MM-DD");
    const insightsQuery = useQuery(
        [
            "key-insights",
            staffId,
            hotelIds,
            startDate,
            endDate,
            keyGroup,
            page,
            searchQuery,
            sort,
        ],
        async ({ signal }) => {
            const response = await HTTP(
                "/dashboard/getKeyInsights",
                {
                    staff_id: staffId,
                    hotel_ids: hotelIds,
                    start_date: startDate,
                    end_date: endDate,
                    page,
                    page_size: 10,
                    key_group: keyGroup,
                    search: searchQuery,
                    sort_by: sort.field,
                    sort_order: sort.order,
                },
                "POST",
                { signal },
            );
            if (!response?.success || !response?.data?.summary)
                throw new Error(
                    response?.message || "Unable to load key insights.",
                );
            return response.data;
        },
        {
            enabled: Boolean(staffId),
            retry: false,
            refetchOnWindowFocus: false,
        },
    );
    const summary = insightsQuery.data?.summary || {};
    const isRefreshing = insightsQuery.isFetching || hotelsQuery.isFetching;
    const handleRefresh = async () => {
        // The ref closes the gap before query state reaches the next render.
        if (!staffId || isRefreshing || refreshInFlightRef.current) return;
        refreshInFlightRef.current = true;
        try {
            await Promise.allSettled([
                insightsQuery.refetch({ cancelRefetch: false }),
                hotelsQuery.refetch({ cancelRefetch: false }),
            ]);
        } finally {
            refreshInFlightRef.current = false;
        }
    };
    // Older API deployments omit this category even though it is included in
    // totalKeys. Keep the displayed categories consistent during rollout.
    const otherKeys =
        summary.otherKeys ??
        (summary.totalKeys != null &&
        summary.newKeys != null &&
        summary.reissuedKeys != null
            ? Number(summary.totalKeys) -
              Number(summary.newKeys) -
              Number(summary.reissuedKeys)
            : null);
    const visibleRecords = insightsQuery.data?.records || [];
    const recordTotal = insightsQuery.data?.total || 0;
    const filteredKeys = insightsQuery.data?.filteredKeys;
    const changeKeyGroup = (value) => {
        setKeyGroup(value);
        setPage(1);
        setSearch("");
        setSearchQuery("");
        setSort({ field: "issuedAt", order: "descend" });
    };
    const openTransactions = (group) => {
        changeKeyGroup(group);
        transactionsRef.current?.scrollIntoView?.({
            behavior: "smooth",
            block: "start",
        });
    };
    const changeRange = (value) => {
        setRange(value);
        setPage(1);
        if (value !== "custom") {
            setDates([
                dayjs()
                    .subtract(Number(value) - 1, "day")
                    .startOf("day"),
                dayjs().endOf("day"),
            ]);
        }
    };
    const selectedHotels = hotels.filter((hotel) =>
        hotelIds.includes(Number(hotel.hotel_id ?? hotel.id)),
    );
    const hotelLabel =
        selectedHotels.length === 1
            ? `${selectedHotels[0].hotel_name || selectedHotels[0].name}${
                  selectedHotels[0].city ? ` · ${selectedHotels[0].city}` : ""
              }`
            : hotelIds.length
            ? `${hotelIds.length} hotels selected`
            : "All accessible hotels";
    const metrics = [
        {
            title: "Total keys issued",
            group: "all",
            value: formatCount(summary.totalKeys),
            detail: "Keys recorded across all operations",
            icon: <KeyOutlined />,
            tone: "purple",
        },
        {
            title: "New keys at check-in",
            group: "new",
            value: formatCount(summary.newKeys),
            detail: "Check-in and walk-in keys",
            icon: <CreditCardOutlined />,
            tone: "green",
        },
        {
            title: "Duplicate / re-issued keys",
            group: "reissued",
            value: formatCount(summary.reissuedKeys),
            detail: "Additional or replacement keys",
            icon: <ReloadOutlined />,
            tone: "blue",
        },
        {
            title: "Other keys",
            group: "other",
            value: formatCount(otherKeys),
            detail: "Keys from pickup, checkout and other operations",
            icon: <AppstoreOutlined />,
            tone: "neutral",
        },
        {
            title: "Re-issued per 100 check-ins",
            group: "reissued",
            value:
                summary.per100Checkins == null
                    ? "—"
                    : Number(summary.per100Checkins).toFixed(1),
            detail: `Across ${formatCount(
                summary.checkins,
            )} arrivals with keys`,
            icon: <PercentageOutlined />,
            tone: "orange",
        },
    ];
    const columns = [
        {
            title: "Transaction ID",
            dataIndex: "id",
            key: "id",
            width: 220,
            sorter: true,
            sortOrder: sort.field === "id" ? sort.order : null,
            render: (value) => (
                <span className="key-insights-transaction-id">{value}</span>
            ),
        },
        {
            title: "Transaction time",
            dataIndex: "issuedAt",
            key: "issuedAt",
            width: 180,
            sorter: true,
            sortOrder: sort.field === "issuedAt" ? sort.order : null,
            render: (value, record) => (
                <span className="key-insights-time">
                    <Tooltip title={`Hotel time: ${record.timeZone || "UTC"}`}>
                        {dayjs(value)
                            .tz(record.timeZone || "UTC")
                            .format("MMM D, YYYY, h:mm a")}
                    </Tooltip>
                </span>
            ),
        },
        ...(hotelIds.length !== 1
            ? [
                  {
                      title: "Hotel",
                      dataIndex: "hotelName",
                      key: "hotelName",
                      width: 170,
                  },
              ]
            : []),
        {
            title: "Room",
            dataIndex: "room",
            key: "room",
            width: 78,
            sorter: true,
            sortOrder: sort.field === "room" ? sort.order : null,
            render: (value) => value || "—",
        },
        {
            title: "Type",
            dataIndex: "operationType",
            key: "operationType",
            width: 175,
            render: (value) => (
                <Tag>
                    {operationLabels[value] ||
                        (value == null ? "Not recorded" : `Other (${value})`)}
                </Tag>
            ),
        },
        {
            title: "Keys dispensed",
            dataIndex: "keyCount",
            key: "keyCount",
            width: 130,
            align: "right",
            sorter: true,
            sortOrder: sort.field === "keyCount" ? sort.order : null,
            render: formatCount,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (value) => (
                <Tag
                    color={
                        Number(value) === 3
                            ? "green"
                            : Number(value) === 2
                            ? "red"
                            : "default"
                    }
                >
                    {statusLabels[value] || "Not recorded"}
                </Tag>
            ),
        },
    ];

    return (
        <div className={`key-insights-page ${isDark ? "dark" : "light"}`}>
            <div className="key-insights-header">
                <h1>Key insights</h1>
                <div className="key-insights-header-actions">
                    <div className="key-insights-filter-group">
                        <label htmlFor="key-insights-hotels">Hotel</label>
                        <Select
                            id="key-insights-hotels"
                            aria-label="Hotel"
                            mode="multiple"
                            value={hotelIds}
                            allowClear
                            showSearch
                            maxTagCount={1}
                            placeholder="All accessible hotels"
                            loading={hotelsQuery.isLoading}
                            optionFilterProp="label"
                            options={hotels.map((hotel) => ({
                                value: Number(hotel.hotel_id ?? hotel.id),
                                label: `${hotel.hotel_name || hotel.name}${
                                    hotel.city ? ` · ${hotel.city}` : ""
                                }`,
                            }))}
                            onChange={(values) => {
                                setHotelIds(values);
                                setPage(1);
                            }}
                            className="key-insights-hotel-select"
                        />
                    </div>
                    <div className="key-insights-filter-group">
                        <label htmlFor="key-insights-range">Date range</label>
                        <Select
                            id="key-insights-range"
                            aria-label="Date range"
                            value={range}
                            options={rangeOptions}
                            onChange={changeRange}
                            className="key-insights-range-select"
                        />
                    </div>
                    <CustomHeader compact profileOnly hideBack />
                </div>
            </div>

            {range === "custom" && (
                <div className="key-insights-custom-dates">
                    <label htmlFor="key-insights-start">Custom dates</label>
                    <DatePicker.RangePicker
                        id={{
                            start: "key-insights-start",
                            end: "key-insights-end",
                        }}
                        value={dates}
                        allowClear={false}
                        format="MMM D, YYYY"
                        disabledDate={(date) =>
                            date &&
                            (date.isAfter(dayjs(), "day") ||
                                date.isBefore(
                                    dayjs().subtract(179, "day"),
                                    "day",
                                ))
                        }
                        onChange={(values) => {
                            if (values?.[0] && values?.[1]) {
                                setDates(values);
                                setPage(1);
                            }
                        }}
                    />
                </div>
            )}

            <div className="key-insights-context">
                <div>
                    <h2>Keys issued</h2>
                    <p>
                        {hotelLabel}
                        <span aria-hidden="true"> · </span>
                        {dates[0].format("MMM D, YYYY")} –{" "}
                        {dates[1].format("MMM D, YYYY")}
                    </p>
                </div>
                <Button
                    icon={<ReloadOutlined />}
                    loading={isRefreshing}
                    disabled={!staffId || isRefreshing}
                    onClick={handleRefresh}
                >
                    Refresh
                </Button>
            </div>
            {(!staffId || insightsQuery.isError || hotelsQuery.isError) && (
                <Alert
                    className="key-insights-error"
                    type="error"
                    showIcon
                    message={
                        !staffId
                            ? "Sign in to view hotel key activity"
                            : "Unable to load live key data"
                    }
                    description={
                        !staffId
                            ? "Your hotel access is determined by your signed-in account."
                            : "We couldn’t load hotel key activity. Please try again in a moment."
                    }
                    action={
                        <Button
                            size="small"
                            loading={isRefreshing}
                            disabled={!staffId || isRefreshing}
                            onClick={handleRefresh}
                        >
                            Retry
                        </Button>
                    }
                />
            )}

            <div className="key-insights-metrics">
                {metrics.map((metric) => (
                    <Card
                        key={metric.title}
                        className={`key-insights-metric ${metric.tone} ${
                            keyGroup === metric.group ? "selected" : ""
                        }`}
                        role="button"
                        tabIndex={insightsQuery.isSuccess ? 0 : -1}
                        aria-label={`View ${metric.title.toLowerCase()} transactions`}
                        aria-pressed={keyGroup === metric.group}
                        aria-disabled={!insightsQuery.isSuccess}
                        aria-controls="key-insights-transactions"
                        onClick={() => {
                            if (insightsQuery.isSuccess)
                                openTransactions(metric.group);
                        }}
                        onKeyDown={(event) => {
                            if (
                                (event.key === "Enter" || event.key === " ") &&
                                insightsQuery.isSuccess
                            ) {
                                event.preventDefault();
                                openTransactions(metric.group);
                            }
                        }}
                        loading={insightsQuery.isLoading && Boolean(staffId)}
                    >
                        <div className="key-insights-metric-heading">
                            <span>{metric.title}</span>
                            <span className="key-insights-metric-icon">
                                {metric.icon}
                            </span>
                        </div>
                        <div className="key-insights-metric-value">
                            {metric.value}
                        </div>
                        <div className="key-insights-metric-detail">
                            {metric.detail}
                        </div>
                        <span className="key-insights-drilldown-link">
                            View transactions →
                        </span>
                    </Card>
                ))}
            </div>

            <div
                id="key-insights-transactions"
                ref={transactionsRef}
                className="key-insights-transactions-anchor"
            >
                <Card className="key-insights-records-card">
                    <div className="key-insights-section-heading">
                        <h2>Key dispensing transactions</h2>
                        <div className="key-insights-record-counts">
                            {filteredKeys != null && (
                                <Tag>
                                    {formatCount(filteredKeys)} keys dispensed
                                </Tag>
                            )}
                            <Tag>{formatCount(recordTotal)} transactions</Tag>
                        </div>
                    </div>
                    <p className="key-insights-section-description">
                        Transactions with recorded key dispensing. Each row
                        shows the key quantity recorded for that transaction;
                        times use the hotel’s time zone.
                    </p>
                    <div className="key-insights-table-filters">
                        <Select
                            aria-label="Key transaction category"
                            value={keyGroup}
                            options={keyGroupOptions}
                            onChange={changeKeyGroup}
                        />
                        <Input
                            aria-label="Search key transactions"
                            placeholder="Search room, hotel or transaction ID"
                            prefix={<SearchOutlined />}
                            value={search}
                            allowClear
                            onChange={(event) => setSearch(event.target.value)}
                        />
                        {search && (
                            <Button
                                type="text"
                                onClick={() => {
                                    setSearch("");
                                    setSearchQuery("");
                                    setPage(1);
                                }}
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                    <Table
                        rowKey={(record) => `${record.hotelId}:${record.id}`}
                        size="small"
                        columns={columns}
                        dataSource={visibleRecords}
                        loading={insightsQuery.isFetching}
                        onChange={(pagination, filters, sorter, extra) => {
                            if (extra.action === "sort") {
                                setSort({
                                    field: sorter.order
                                        ? sorter.field
                                        : "issuedAt",
                                    order: sorter.order || "descend",
                                });
                                setPage(1);
                            }
                        }}
                        scroll={{ x: hotelIds.length !== 1 ? 1100 : 950 }}
                        pagination={{
                            current: page,
                            total: recordTotal,
                            pageSize: 10,
                            showSizeChanger: false,
                            onChange: setPage,
                            hideOnSinglePage: true,
                            showTotal: (total, [start, end]) =>
                                `${start}–${end} of ${formatCount(
                                    total,
                                )} transactions`,
                        }}
                        locale={{
                            emptyText: (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        insightsQuery.isError
                                            ? "Key transactions could not be loaded"
                                            : searchQuery
                                            ? "No key transactions match your search"
                                            : "No keys were dispensed for these filters"
                                    }
                                />
                            ),
                        }}
                    />
                </Card>
            </div>
        </div>
    );
};

export { KeyInsights };
